<template>
  <div class="q-chart-card av-mp-card" v-if="hayDatos">
    <div class="q-chart-head">
      <div>
        <p class="q-chart-title">Métodos de pago</p>
        <p class="q-chart-subtitle">Distribución EUR / USD por método</p>
      </div>
      <div class="mp-legend">
        <span class="mp-legend-dot" style="background:var(--q-gold,#d4af37)"></span>
        <span class="text-xs">EUR</span>
        <span class="mp-legend-dot" style="background:#26c6da;margin-left:10px"></span>
        <span class="text-xs">USD</span>
      </div>
    </div>

    <div class="mp-body">
      <div class="mp-chart-col">
        <div class="mp-canvas-wrap" ref="canvasWrap">
          <canvas ref="canvas"></canvas>
        </div>
      </div>

      <div class="mp-table-col">
        <table class="mp-table">
          <thead>
            <tr>
              <th class="mp-th-method">Método</th>
              <th class="mp-th-cur tc-th--eur">EUR</th>
              <th class="mp-th-cur tc-th--usd">USD</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in topMetodos" :key="m.metodo">
              <td class="mp-td-method">
                <span class="mp-icon">{{ metodoPagoIcon(m.metodo) }}</span>
                <span class="mp-nombre">{{ m.metodo }}</span>
              </td>
              <td class="mp-td-cur">
                <template v-if="m.ventas_eur > 0">
                  <span class="mp-count-badge eur">{{ m.ventas_eur }}</span>
                  <span class="mp-amount eur">{{ fmt(m.importe_eur, 'EUR') }}</span>
                </template>
                <span v-else class="mp-na">—</span>
              </td>
              <td class="mp-td-cur">
                <template v-if="m.ventas_usd > 0">
                  <span class="mp-count-badge usd">{{ m.ventas_usd }}</span>
                  <span class="mp-amount usd">{{ fmt(m.importe_usd, 'USD') }}</span>
                </template>
                <span v-else class="mp-na">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

const ICONS = {
  'Visa': '💳', 'Mastercard': '💳', 'Amex': '💳', 'Discover': '💳',
  'Unionpay': '💳', 'Card': '💳',
  'Google Pay': '🟢', 'Apple Pay': '🍎', 'Samsung Pay': '📱',
  'PayPal': '🅿️', 'Amazon Pay': '📦',
  'Link': '🔗', 'Klarna': '🛍️',
  'SEPA Debit': '🏦', 'iDEAL': '🏦', 'Sofort': '⚡', 'Bancontact': '🏦',
};

export default {
  name: 'AvMetodosPago',
  props: {
    datos: { type: Array, default: () => [] },
  },
  data() {
    return { chartInstance: null };
  },
  computed: {
    hayDatos() { return this.datos && this.datos.length > 0; },
    topMetodos() { return this.datos.slice(0, 8); },
  },
  watch: {
    datos: {
      deep: true,
      handler() { this.$nextTick(() => requestAnimationFrame(() => this.renderChart())); },
    },
  },
  mounted() {
    this.$nextTick(() => requestAnimationFrame(() => this.renderChart()));
  },
  beforeUnmount() {
    this.chartInstance?.destroy();
  },
  methods: {
    metodoPagoIcon(m) { return ICONS[m] || '💰'; },

    fmt(n, cur) {
      return Number(n || 0).toLocaleString('es-ES', {
        style: 'currency', currency: cur, maximumFractionDigits: 0,
      });
    },

    renderChart() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.topMetodos.length) return;
      this.chartInstance?.destroy();

      const labels  = this.topMetodos.map(m => m.metodo);
      const dataEur = this.topMetodos.map(m => Number(m.ventas_eur  || 0));
      const dataUsd = this.topMetodos.map(m => Number(m.ventas_usd  || 0));
      const amtEur  = this.topMetodos.map(m => Number(m.importe_eur || 0));
      const amtUsd  = this.topMetodos.map(m => Number(m.importe_usd || 0));

      const barH = 14;
      if (this.$refs.canvasWrap) {
        this.$refs.canvasWrap.style.height = `${labels.length * (barH * 2 + 16) + 40}px`;
      }

      this.chartInstance = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'EUR',
              data: dataEur,
              backgroundColor: 'rgba(245,166,35,.85)',
              hoverBackgroundColor: 'rgba(245,166,35,1)',
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: barH,
              extra: amtEur,
            },
            {
              label: 'USD',
              data: dataUsd,
              backgroundColor: 'rgba(38,198,218,.75)',
              hoverBackgroundColor: 'rgba(38,198,218,.95)',
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: barH,
              extra: amtUsd,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const v   = ctx.parsed.x;
                  const amt = ctx.dataset.extra?.[ctx.dataIndex] ?? 0;
                  const cur = ctx.dataset.label;
                  const fmtAmt = Number(amt).toLocaleString('es-ES', {
                    style: 'currency', currency: cur, maximumFractionDigits: 0,
                  });
                  return ` ${v} transacciones · ${fmtAmt}`;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: false,
              grid: { color: 'rgba(255,255,255,.06)' },
              ticks: { color: 'rgba(231,237,245,.5)', font: { size: 10 }, precision: 0 },
              border: { dash: [4, 4] },
            },
            y: {
              stacked: false,
              grid: { display: false },
              ticks: { color: 'rgba(231,237,245,.75)', font: { size: 11, weight: '600' } },
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.av-mp-card { height: 100%; }

.mp-body {
  display: flex;
  gap: 24px;
  padding: 12px 16px 16px;
  flex-wrap: wrap;
}

.mp-chart-col { flex: 1 1 280px; min-width: 240px; }
.mp-canvas-wrap {
  position: relative;
  width: 100%;
  min-height: 120px;
}
.mp-canvas-wrap canvas { width: 100% !important; height: 100% !important; }

.mp-table-col { flex: 0 0 auto; min-width: 270px; }

.mp-legend {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--q-muted, #8892a4);
}
.mp-legend-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.mp-table thead th {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
  padding: 4px 8px;
  text-align: right;
  color: var(--q-muted, #8892a4);
  border-bottom: 1px solid var(--q-border, #2a2f3d);
}
.mp-th-method { text-align: left; }
.tc-th--eur { color: var(--q-gold, #d4af37) !important; }
.tc-th--usd { color: #26c6da !important; }

.mp-table tbody td {
  padding: 7px 8px;
  border-bottom: 1px solid var(--q-border, #2a2f3d);
  vertical-align: middle;
}
.mp-td-method {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--q-text, #e8eaf0);
  white-space: nowrap;
}
.mp-icon { font-size: 13px; flex-shrink: 0; }
.mp-nombre { font-size: 12px; }

.mp-td-cur { text-align: right; white-space: nowrap; }
.mp-count-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 20px;
  margin-right: 4px;
  font-variant-numeric: tabular-nums;
}
.mp-count-badge.eur { background: rgba(245,166,35,.18); color: var(--q-gold, #d4af37); }
.mp-count-badge.usd { background: rgba(38,198,218,.15); color: #26c6da; }

.mp-amount {
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.mp-amount.eur { color: var(--q-gold, #d4af37); }
.mp-amount.usd { color: #26c6da; }
.mp-na { color: var(--q-muted, #8892a4); font-size: 12px; }

@media (max-width: 768px) {
  .mp-body { flex-direction: column; gap: 16px; }
  .mp-table-col { min-width: unset; width: 100%; }
}
</style>
